/**
 * EF-CLIENT-ONBOARDING-FRONTEND-V1: Client Login Page
 * EF-PWA-BRANDING-V1: Added dynamic company branding
 * EF-PWA-BRANDING-V1-STEP2: Client portal ALWAYS uses tenant branding
 * 
 * Route: /client/login
 * Features:
 * - Email + password login
 * - POST /api/client/auth/login
 * - Redirect to /client on success
 * - Link to "Forgot password"
 * - Dynamic company logo from settings (always tenant branding for client portal)
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, Lock, Mail, AlertCircle, Building2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { clientLogin } from "../../lib/api";
// EF-PWA-BRANDING-V1-STEP2: Import branding defaults
import { EVENTSFLO_BRANDING } from "../../lib/tenantContext";
// EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1
import PortalChooserLinks from "../../components/PortalChooserLinks";
// EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Token storage key for client portal
const CLIENT_TOKEN_KEY = "eventsflo_client_token";

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
  const [portalMismatch, setPortalMismatch] = useState(null);

  // EF-PWA-BRANDING-V1-STEP2: Client portal ALWAYS fetches tenant branding
  const [branding, setBranding] = useState(EVENTSFLO_BRANDING);

  // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
  // Prefill email from `?email=` so the cross-portal redirect carries
  // the email forward. Never prefill password.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const prefill = (params.get("email") || "").trim().toLowerCase();
    if (prefill) setEmail(prefill);
  }, [location.search]);

  // Client portal always fetches tenant branding (no context check needed)
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/branding`);
        if (response.ok) {
          const data = await response.json();
          setBranding({
            ...data,
            is_platform_branding: false // Client portal is always tenant context
          });
        }
      } catch (err) {
        console.log("Branding fetch failed, using defaults");
        setBranding(EVENTSFLO_BRANDING);
      }
    };
    fetchBranding();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPortalMismatch(null);

    if (!email || !password) {
      setError(t("clientPortal.login.missingCredentials"));
      return;
    }
    
    setLoading(true);
    // EVENTSFLO-CLIENT-LOGIN-STALE-PWA-BUILD-AUTH-DIAGNOSTIC-V1
    // Diagnostic only — logs the EMAIL the browser is actually sending
    // (so the user can verify it isn't autofilled to something else) and
    // the password LENGTH (NEVER the value). Helps disambiguate "I
    // typed the right thing" from "my browser autofilled a stale value".
    console.log(
      "[CLIENT AUTH] submit email=%s emailLen=%d passwordLen=%d apiBase=%s",
      email,
      email.length,
      password.length,
      process.env.REACT_APP_BACKEND_URL || "(unset)",
    );
    
    try {
      const response = await clientLogin(email, password);
      const data = response.data || response;
      
      if (data.access_token) {
        // Store token
        localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
        
        console.log("[CLIENT AUTH] Login successful, redirecting to portal");
        toast.success(t("clientPortal.login.loginSuccessful"), {
          description: t("clientPortal.login.welcomeBack", { name: data.user?.name || "" }),
        });
        
        // Redirect to client portal
        navigate("/client", { replace: true });
      } else {
        setError(t("clientPortal.login.noToken"));
        console.log("[CLIENT AUTH] Login failed - no token");
      }
    } catch (err) {
      console.error("[CLIENT AUTH] Login error:", err);
      // EVENTSFLO-CLIENT-PORTAL-GRIMALDI-LOGIN-REGRESSION-AUDIT-V2
      // Log the API base URL on every login failure so a recurring
      // "Invalid email or password" report can be triaged for
      // frontend/backend environment mismatch without round-tripping
      // through support. No secrets are logged.
      const apiBase = process.env.REACT_APP_BACKEND_URL || "(unset)";
      console.error(
        "[CLIENT AUTH] api_base=%s status=%s",
        apiBase,
        err.response?.status ?? "(no response)",
      );
      // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
      const raw = err.response?.data?.detail;
      if (raw && typeof raw === "object" && raw.error === "wrong_portal_admin_account") {
        setPortalMismatch({
          error: raw.error,
          redirect: raw.redirect || "/login",
        });
        return;
      }
      const errorMsg =
        (typeof raw === "string" && raw) || raw?.message || err.message || t("clientPortal.login.loginFailed");
      setError(errorMsg);
      toast.error(t("clientPortal.login.loginFailed"), { description: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-sky-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-sky-500/3 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="client-login-card">
        <CardHeader className="text-center pb-2">
          {/* EF-PWA-BRANDING-V1: Dynamic Logo / Brand */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.company_name || "Company"} 
                className="h-12 w-auto max-w-[180px] object-contain"
                data-testid="company-logo"
                onError={(e) => {
                  // Fallback to icon if logo fails to load
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="p-2 rounded-lg bg-sky-500/10 border border-sky-500/20"><svg class="h-8 w-8 text-sky-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg></div>';
                }}
              />
            ) : (
              <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/20">
                <Building2 className="h-8 w-8 text-sky-400" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">{t("clientPortal.portalTitle")}</CardTitle>
          <CardDescription>
            {branding.has_custom_branding
              ? t("clientPortal.login.brandedSubtitle", { company: branding.company_name })
              : t("clientPortal.login.subtitle")
            }
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1 */}
            {portalMismatch && (
              <Alert
                className="border-amber-500/40 bg-amber-500/10 text-amber-200"
                data-testid="client-login-portal-mismatch"
              >
                <AlertCircle className="h-4 w-4 text-amber-300" />
                <AlertDescription className="space-y-2">
                  <p className="text-sm">
                    {t("clientPortal.login.portalMismatchMessage")}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(portalMismatch.redirect)}
                    className="inline-flex items-center rounded border border-amber-500/40 px-3 py-1 text-xs font-medium text-amber-100 hover:bg-amber-500/20"
                    data-testid="client-login-portal-mismatch-cta"
                  >
                    {t("clientPortal.login.portalMismatchCta")}
                  </button>
                </AlertDescription>
              </Alert>
            )}
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="border-red-500/50 bg-red-500/10" data-testid="login-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground">
                {t("clientPortal.login.emailLabel")}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t("clientPortal.login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-background/50"
                  disabled={loading}
                  data-testid="login-email"
                />
              </div>
            </div>
            
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-muted-foreground">
                {t("clientPortal.login.passwordLabel")}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("clientPortal.login.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-background/50"
                  disabled={loading}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/client/reset-password"
                className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
                data-testid="forgot-password-link"
              >
                {t("clientPortal.login.forgotPassword")}
              </Link>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full bg-sky-600 hover:bg-sky-500"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("clientPortal.login.signingIn")}
                </>
              ) : (
                t("clientPortal.login.loginButton")
              )}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              {t("clientPortal.login.needAccess")}
            </p>

            {/* EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1: Portal chooser
                (Admin + Crew links + Request quote). */}
            <PortalChooserLinks currentPortal="client" email={email} />
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

